import { PatientLayout } from "@/components/layout/patient-layout";
import { useListMedications } from "@workspace/api-client-react";
import { LoadingScreen } from "@/components/ui/loading-screen";
import { UkHealthDisclaimer } from "@/components/ui/uk-health-disclaimer";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Link } from "wouter";
import { Search, ArrowRight, Pill, ArrowUpDown } from "lucide-react";
import { useMemo } from "react";
import { useSearchParam } from "@/hooks/use-search-param";
import { useScrollRestore } from "@/hooks/use-scroll-restore";
import { Highlight } from "@/components/ui/highlight";

const PATIENT_DRUG_GROUPS: Record<string, string> = {
  cDMARD: "Disease-modifying drugs (cDMARDs)",
  bDMARD: "Biologic drugs (bDMARDs)",
  tsDMARD: "Targeted synthetic drugs (tsDMARDs)",
  Steroid: "Steroids & anti-inflammatories",
  NSAID: "Pain relief & NSAIDs",
  Other: "Other medicines",
};

function classifyDrug(drugClass?: string | null): string {
  if (!drugClass) return "Other";
  const dc = drugClass.toLowerCase();
  if (dc.includes("biologic") || dc.includes("bdmard")) return "bDMARD";
  if (dc.includes("tsdmard") || dc.includes("jak") || dc.includes("targeted")) return "tsDMARD";
  if (dc.includes("dmard") || dc.includes("disease-modifying") || dc.includes("conventional")) return "cDMARD";
  if (dc.includes("steroid") || dc.includes("corticosteroid") || dc.includes("glucocorticoid")) return "Steroid";
  if (dc.includes("nsaid") || dc.includes("anti-inflam") || dc.includes("analgesic") || dc.includes("pain")) return "NSAID";
  return "Other";
}

export default function PatientKnowYourMedication() {
  useScrollRestore("patient-kym");
  const [search, setSearch] = useSearchParam("q", "");
  const [activeGroup, setActiveGroup] = useSearchParam("group", "", { storageKey: "patient-kym:group" });
  const [sort, setSort] = useSearchParam("sort", "az", { storageKey: "patient-kym:sort" });
  const { data: medications, isLoading } = useListMedications();

  const filtered = useMemo(() => {
    if (!medications) return [];
    const base = medications.filter(m => {
      const q = search.toLowerCase();
      const matchesSearch = !search ||
        m.name.toLowerCase().includes(q) ||
        (m.genericName ?? "").toLowerCase().includes(q) ||
        (m.patientSummary ?? "").toLowerCase().includes(q);
      const group = classifyDrug(m.drugClass);
      const matchesGroup = !activeGroup || group === activeGroup;
      return matchesSearch && matchesGroup;
    });
    if (sort === "za") return [...base].sort((a, b) => b.name.localeCompare(a.name));
    return [...base].sort((a, b) => a.name.localeCompare(b.name));
  }, [medications, search, activeGroup, sort]);

  const grouped = useMemo(() => {
    const groupKeys = Object.keys(PATIENT_DRUG_GROUPS);
    return groupKeys
      .map(key => ({
        key,
        label: PATIENT_DRUG_GROUPS[key],
        items: filtered.filter(m => classifyDrug(m.drugClass) === key),
      }))
      .filter(g => g.items.length > 0);
  }, [filtered]);

  const resultCount = filtered.length;

  if (isLoading) return <PatientLayout title="Know Your Medication"><LoadingScreen /></PatientLayout>;

  return (
    <PatientLayout title="Know Your Medication">
      <div className="mb-8 max-w-3xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-indigo-100 p-2.5 rounded-2xl">
            <Pill className="h-6 w-6 text-indigo-600" />
          </div>
          <p className="text-lg text-slate-600 leading-relaxed">
            Clear, plain-language guides to the medicines used in rheumatology — what they do, how to take them, and what to watch for.
          </p>
        </div>
        <UkHealthDisclaimer compact />
      </div>

      <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center mb-6">
        <div className="relative max-w-sm flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search medicines…"
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveGroup("")}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
              activeGroup === ""
                ? "bg-indigo-700 text-white border-indigo-700 shadow-sm"
                : "bg-white text-slate-600 border-slate-200 hover:border-indigo-400"
            }`}
          >
            All
          </button>
          {Object.entries(PATIENT_DRUG_GROUPS).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setActiveGroup(activeGroup === key ? "" : key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                activeGroup === key
                  ? "bg-indigo-700 text-white border-indigo-700 shadow-sm"
                  : "bg-white text-slate-600 border-slate-200 hover:border-indigo-400"
              }`}
            >
              {key}
            </button>
          ))}
          <button
            onClick={() => setSort(sort === "az" ? "za" : "az")}
            className="px-3 py-1.5 rounded-full text-xs font-semibold border bg-white text-slate-600 border-slate-200 hover:border-indigo-400 inline-flex items-center gap-1.5 transition-all"
          >
            <ArrowUpDown className="h-3 w-3" />
            {sort === "az" ? "A–Z" : "Z–A"}
          </button>
        </div>
      </div>

      <p className={`mb-4 text-sm ${resultCount === 0 ? "text-rose-500" : "text-slate-500"}`}>
        {resultCount === 0 ? "No medicines found" : `Showing ${resultCount} ${resultCount === 1 ? "medicine" : "medicines"}`}
      </p>

      {grouped.length === 0 && (
        <div className="py-16 text-center text-slate-500 bg-white rounded-2xl border border-slate-200">
          No medicines found{search ? ` matching "${search}"` : ""}.
        </div>
      )}

      {grouped.map(group => (
        <div key={group.key} className="mb-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-indigo-100 p-1.5 rounded-lg">
              <Pill className="h-4 w-4 text-indigo-600" />
            </div>
            <h2 className="text-lg font-bold text-slate-800">{group.label}</h2>
            <span className="text-xs font-semibold bg-indigo-50 text-indigo-600 border border-indigo-200 rounded-full px-2.5 py-0.5">
              {group.items.length}
            </span>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {group.items.map(m => (
              <Link key={m.id} href={`/patient/know-your-medication/${m.id}`}>
                <Card className="h-full hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer rounded-2xl group">
                  <CardContent className="p-6 flex flex-col h-full">
                    <div className="mb-2">
                      <h3 className="font-bold text-slate-900 group-hover:text-indigo-600 transition-colors"><Highlight text={m.name} query={search} className="text-rose-700" /></h3>
                      {m.genericName && m.genericName !== m.name && (
                        <p className="text-xs text-slate-400 mt-0.5">Also: <Highlight text={m.genericName} query={search} className="text-indigo-600" /></p>
                      )}
                    </div>
                    <p className="text-slate-600 text-sm line-clamp-3 flex-1 leading-relaxed mb-4">
                      <Highlight text={m.patientSummary ?? m.patientNotes ?? ""} query={search} className="text-indigo-600" />
                    </p>
                    <div className="mt-auto pt-3 border-t border-slate-100 flex items-center text-sm font-medium text-indigo-600">
                      Read guide <ArrowRight className="h-4 w-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      ))}
    </PatientLayout>
  );
}
